import { supabase } from "../supabase";
import {
  appSettingsPatchToDbUpdate,
  organizationRowToAppSettings,
} from "./organizationSettings";

export const ORGANIZATION_ROW_SELECT = `
      id,
      company_name,
      legal_name,
      logo_url,
      address,
      phone,
      email,
      website,
      currency,
      timezone,
      locale,
      business_number,
      gst_hst_registration_number,
      qst_registration_number,
      additional_registration_label,
      additional_registration_number,
      tax_settings,
      settings,
      status,
      created_at,
      updated_at
    `;

/**
 * Resolve the platform singleton organization id (invitations / membership
 * tie-break / documented singleton settings only — not operational Gate 4 queries).
 *
 * RG-01: reads via SECURITY DEFINER RPC only — never SELECT crewpilot_app_settings.
 * RG-06: do NOT fall back to "first organizations row" (wrong-tenant substitution).
 */
export async function getDefaultOrganizationId(client = supabase) {
  const { data: rpcId, error: rpcError } = await client.rpc(
    "cp_get_default_organization_id",
  );

  if (rpcError) {
    console.warn(
      "[organizationHelpers] cp_get_default_organization_id failed",
      rpcError,
    );
  }

  return rpcId || null;
}

/**
 * Load the default organization row.
 */
export async function getOrganization(organizationId, client = supabase) {
  const { data, error } = await client
    .from("organizations")
    .select(ORGANIZATION_ROW_SELECT)
    .eq("id", organizationId)
    .single();

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Load default organization for the app singleton.
 */
export async function getDefaultOrganization(client = supabase) {
  const organizationId = await getDefaultOrganizationId(client);
  if (!organizationId) {
    return null;
  }
  return getOrganization(organizationId, client);
}

/**
 * Resolve organization row for the actor's membership.
 * RG-06: do not silently substitute the platform default organization when
 * membership org is missing or fails to load.
 */
export function resolveOrganizationIdFromActor(actor) {
  return actor?.organization_id ?? null;
}

export async function resolveOrganizationForActor(actor, client = supabase) {
  const membershipOrgId = resolveOrganizationIdFromActor(actor);
  if (!membershipOrgId) {
    return null;
  }

  return getOrganization(membershipOrgId, client);
}

/**
 * Persist organization changes from an app-settings patch.
 */
export async function updateOrganization(
  organizationId,
  patch,
  currentRow,
  client = supabase,
) {
  const dbPatch = appSettingsPatchToDbUpdate(patch, currentRow);

  const { data, error } = await client
    .from("organizations")
    .update(dbPatch)
    .eq("id", organizationId)
    .select(ORGANIZATION_ROW_SELECT)
    .single();

  if (error) {
    throw error;
  }

  return {
    row: data,
    settings: organizationRowToAppSettings(data),
  };
}

/**
 * Save the Organization Work Schedule (organizations.settings.work_schedule).
 * doc = { version, working_weekdays, minutes_per_day, non_working_dates } or
 * null to return to the default. Reads the current settings document first so
 * only this key changes. The database validates and stamps it and RLS limits
 * the write to owners/admins of the organization; a refused write throws (no
 * local-only success).
 */
export async function saveOrganizationWorkSchedule(organizationId, doc, client = supabase) {
  if (!organizationId) throw new Error("No organization loaded.");
  const { data: current, error: readError } = await client
    .from("organizations")
    .select(ORGANIZATION_ROW_SELECT)
    .eq("id", organizationId)
    .single();
  if (readError) throw readError;
  const settings =
    current?.settings && typeof current.settings === "object" && !Array.isArray(current.settings)
      ? { ...current.settings }
      : {};
  if (doc == null) delete settings.work_schedule;
  else settings.work_schedule = doc;
  const { data, error } = await client
    .from("organizations")
    .update({ settings })
    .eq("id", organizationId)
    .select(ORGANIZATION_ROW_SELECT)
    .single();
  if (error) throw error;
  return { row: data, settings: organizationRowToAppSettings(data) };
}


/**
 * A1 Self-Service Signup: a confirmed, signed-in user with no organization creates their company and becomes its
 * Organization Owner (server: cp_self_service_create_organization -> cp_create_organization_with_owner).
 */
export const SELF_SERVICE_ORG_ERRORS = Object.freeze({
  email_not_confirmed: "Confirm your email address first, then sign in again to create your company.",
  already_member: "You already belong to an organization. Ask its owner or admin for access instead.",
  already_created: "You have already created an organization with this account.",
});

export async function createSelfServiceOrganization({ companyName, ownerName } = {}, client = supabase) {
  const name = String(companyName || "").trim();
  if (name.length < 2 || name.length > 120) {
    return { data: null, error: { message: "Company name must be 2 to 120 characters." } };
  }
  const { data, error } = await client.rpc("cp_self_service_create_organization", {
    p_company_name: name,
    p_owner_name: String(ownerName || "").trim() || null,
  });
  if (error) {
    const friendly = SELF_SERVICE_ORG_ERRORS[error.hint];
    return { data: null, error: { ...error, message: friendly || error.message || "Could not create the organization." } };
  }
  return { data, error: null };
}
